/**
 * @fileoverview Optional certifications and volume fields for requirement creation.
 * @module components/RequirementCreationView/RequirementOptionalRequirements
 */
import Select from "react-select";
import styles from "./index.module.css";
import RequirementImageUpload from "./RequirementImageUpload.jsx";

const OptionalRequirementsHeader = ({ form }) => {
  const { t, requirement, handleChange } = form;

  return (
    <div className={styles.subtitleContainer}>
      <div className={styles.subtitleRow}>
        <label className={styles.modalSubtitle}>
          {t("components.requirementCreationModal.optionalRequirements")}
        </label>
        <button
          type="button"
          className={styles.createCertificationButton}
          onClick={() => {
            if (requirement.certificaciones.length < 3) {
              handleChange("certificaciones", [
                ...requirement.certificaciones,
                { nombre: "" },
              ]);
            }
          }}
        >
          + {t("components.requirementCreationModal.addCertification")}
        </button>
      </div>
      <span className={styles.optionalRequirementsText}>
        {t("components.requirementCreationModal.warningMessage")}
      </span>
    </div>
  );
};

const CertificationItem = ({ index, originalCertifications, form }) => {
  const { t, requirement, handleChange, setCertifications } = form;

  return (
    <div className={styles.certificationItem}>
      <div className={styles.certificationInputs}>
        <Select
          options={originalCertifications}
          isSearchable={false}
          onChange={(selectedOption) => {
            if (selectedOption) {
              const updatedCertifications = [...requirement.certificaciones];
              updatedCertifications[index] = selectedOption;
              handleChange("certificaciones", updatedCertifications);
              setCertifications(
                originalCertifications.filter(
                  (cert) =>
                    cert._id !== selectedOption._id &&
                    requirement.certificaciones.findIndex(
                      (c) => c._id === cert._id,
                    ) === -1,
                ),
              );
            }
          }}
          placeholder={t(
            "components.requirementCreationModal.certificationsPlaceholder",
          )}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              borderRadius: "8px",
              width: "100%",
              height: "30px",
              fontSize: "16px",
              cursor: "pointer",
              textAlign: "left",
              borderColor: "#909092",
            }),
            container: (baseStyles) => ({
              ...baseStyles,
              width: "70%",
            }),
            valueContainer: (baseStyles) => ({
              ...baseStyles,
              maxWidth: "300px",
            }),
            input: (baseStyles) => ({
              ...baseStyles,
            }),
            option: (baseStyles) => ({
              ...baseStyles,
              cursor: "pointer",
            }),
            indicatorSeparator: () => ({ display: "none" }),
          }}
          getOptionValue={(option) => option._id}
          formatOptionLabel={(option) => <span>{option.nombre}</span>}
        />
      </div>
      <button
        className={styles.deleteCertificationButton}
        onClick={() => {
          const updatedCertifications = [...requirement.certificaciones];
          updatedCertifications.splice(index, 1);
          handleChange("certificaciones", updatedCertifications);
        }}
      >
        &times;
      </button>
    </div>
  );
};

const CertificationList = ({ originalCertifications, form }) => {
  const { t, requirement } = form;

  return (
    <div className={styles.certificationsContainer}>
      <div className={styles.certificationList}>
        {requirement.certificaciones.length > 0 && (
          <div className={styles.certificationHeader}>
            <label className={styles.label}>
              {t("components.requirementCreationModal.certifications")}
            </label>
          </div>
        )}
        {requirement.certificaciones.length > 0 &&
          requirement.certificaciones.map((_, index) => (
            <CertificationItem
              key={index}
              index={index}
              originalCertifications={originalCertifications}
              form={form}
            />
          ))}
      </div>
    </div>
  );
};

const VolumeField = ({ form }) => {
  const {
    t,
    requirement,
    handleChange,
    handleFieldChange,
    getFieldValidationState,
    getErrorMessage,
  } = form;

  return (
    <div className={styles.volumeContainer}>
      <label className={styles.label}>
        {t("components.requirementCreationModal.volume")}
      </label>
      <input
        type="text"
        placeholder={t("components.requirementCreationModal.volumePlaceholder")}
        className={`${styles.input} ${
          getFieldValidationState(
            "requerimientoVolumen",
            requirement.volumen || "",
          ) === "error"
            ? styles.inputError
            : ""
        }`}
        value={requirement.volumen || ""}
        onChange={(e) => {
          const value = e.target.value;
          handleChange("volumen", value);
          handleFieldChange("requerimientoVolumen", value);
        }}
        maxLength={100}
      />
      {getErrorMessage("requerimientoVolumen") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("requerimientoVolumen")}
        </p>
      )}
    </div>
  );
};

const RequirementOptionalRequirements = ({ originalCertifications, form }) => (
  <>
    <OptionalRequirementsHeader form={form} />
    <div className={styles.optionalRequirementsSection}>
      <CertificationList
        originalCertifications={originalCertifications}
        form={form}
      />
      <div className={styles.optionalRequirementsRow}>
        <RequirementImageUpload
          imageRef={form.imageRef}
          onDragEnter={form.onDragEnter}
          onDragLeave={form.onDragLeave}
          onDrop={form.onDrop}
          tempImage={form.tempImage}
          imageError={form.imageError}
          onChangeImage={form.handleChangeImage}
          t={form.t}
        />
        <VolumeField form={form} />
      </div>
    </div>
  </>
);

export default RequirementOptionalRequirements;
